import { Icon, Text } from "@blueprintjs/core"
import { useHover } from "@mantine/hooks"
import { DemandIcon } from "components/Icons"
import { useRowSelector } from "components/Molecules/RowSelector"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import { AllDemandsQuery } from "generated/graphql"
import { sumBy } from "lodash-es"
import React, { useMemo, useRef } from "react"
import classNames from "utils/classNames"
import { inr } from "utils/formatters.utils"
import useSelectionActive from "../../useSelectionActive"
import DemandCardFooter from "../DemandCardFooter"
import DemandDoughnut from "../DemandDoughnut"
import styles from "./style.module.css"
dayjs.extend(advancedFormat)

function renderVariety(varietyGroup, cropVariety) {
  if (varietyGroup) return varietyGroup.name
  if (cropVariety && cropVariety.commodity) {
    return `${cropVariety.commodity.name} - (${cropVariety.name})`
  }
}
type DemandCardProps = {
  demandData: AllDemandsQuery["allDemands"]["demands"][0]
  onSelect?: any
  x?: number
  y?: number
}

export function renderDemandDate(date: string, dateTill?: string) {
  const dateDayjs = dayjs(date)
  const dateTillDayjs = dateTill ? dayjs(dateTill) : null
  return (
    <span>
      {dateTillDayjs
        ? `${dateDayjs.format("ddd,Do MMM")} - ${dateTillDayjs.format(
            "ddd, Do MMM"
          )} ${dateTillDayjs.format("'YY")}`
        : `${dateDayjs.format("ddd, Do MMM 'YY")}`}
    </span>
  )
}
const DemandCard = ({ demandData, onSelect, x, y }: DemandCardProps) => {
  const {
    id,
    varietyGroup,
    variety,
    name,
    timeSlots,
    broadcastTime,
    depot,
    gradePrices,
    date,
    isLive,
    dateTill,
    isVerified,
  } = demandData
  const parentRef = useRef<HTMLDivElement>()
  let { ref, hovered: isExpanded } = useHover()
  const isSelectionActive = useSelectionActive()
  const [isSelected, setSelected] = useRowSelector({
    path: "demands",
    payload: { id },
  })

  const defaultGrade = useMemo(
    () => gradePrices.find(gp => gp.isDefault),
    [gradePrices]
  )
  const totalDemand = useMemo(
    () => sumBy(timeSlots, (ts: any) => ts.quantity),
    [timeSlots]
  )
  const totalBooked = useMemo(
    () => sumBy(timeSlots, (ts: any) => ts.bookedQuantity),
    [timeSlots]
  )

  const grade = defaultGrade?.grade
  const price = defaultGrade?.price || 0
  const priceLimit = defaultGrade?.priceLimit
  const priceUnit = defaultGrade?.priceUnit

  return (
    <div ref={parentRef} className="relative h-[450px]">
      <div
        data-x={x}
        data-y={y}
        ref={ref}
        onClick={e => {
          if (isSelectionActive) {
            setSelected()
          }
        }}
        className={classNames(
          "absolute bg-white h-full w-full rounded border-0 cursor-pointer",
          styles["base"],
          !isSelected && "shadow-none",
          timeSlots?.length > 4 ? "hover:h-[130%]" : "hover:h-[125%]",
          "hover:h-[125%] hover:w-[125%] hover:z-10 hover:shadow-2xl transition-transform ease-in-out",
          {
            "border-t-[5px] border-solid border-green-300": isLive,
            "shadow-sm": isSelected ? true : false,
          },
          {
            "border-t-[5px] border-solid border-red-600": !isLive,
          }
        )}
      >
        <header className="flex justify-between mb-2 bg-gray-100 p-3">
          <div className="flex gap-2">
            <div className="text-gray-600 flex items-center">{DemandIcon}</div>
            <div className="flex flex-col">
              <span className="text-sm">
                {renderDemandDate(date, dateTill)}
              </span>
              <span className="text-[13px] font-600">{depot.name}</span>
            </div>
          </div>
          <div
            className={classNames(
              "text-white flex items-center justify-center font-500",
              isLive ? "bg-green-300" : "bg-red-600",
              isExpanded
                ? "w-max h-8 flex gap-1 px-2 rounded-lg"
                : "h-8 w-8 rounded-full"
            )}
          >
            <Icon icon="cell-tower" iconSize={18} />
            {isExpanded && (
              <span>{dayjs(broadcastTime).format("Do MMM 'YY h:mmA")}</span>
            )}
          </div>
        </header>
        <div>
          <div className="text-center px-3 mb-4">
            <div className="content">
              <p
                onClick={e => {
                  // Only open the details dialog if the user is not in selection mode
                  // Else it creates a bad user experience
                  !isSelectionActive && onSelect(id)
                }}
                className="underline mb-1 text-gray-600 text-base"
              >
                {name}
              </p>
              <Text className="mb-1" ellipsize={true}>
                <p className="text-gray-700 font-600 mb-0">
                  <span>{renderVariety(varietyGroup, variety)}</span>
                </p>
              </Text>
              <div className="text-gray-500 font-600">
                {isExpanded ? (
                  <div className="p-3 rounded-lg border border-solid border-l-4 border-gray-300 flex flex-col gap-3">
                    <div className="text-left">
                      <h6 className="mt-0 mx-0 mb-1 text-gray-700 text-xs font-600">
                        Broadcasted Price
                      </h6>
                      <p className="mb-0 text-gray-600 text-center text-base">
                        <span> {grade?.name} @</span>
                        <span>
                          {" "}
                          &#8377;{price}
                          {priceLimit ? `- ${priceLimit}` : null} /
                          {priceUnit.name}
                        </span>
                      </p>
                    </div>
                    <div className="text-left">
                      <h6 className="mt-0 mx-0 mb-1 text-gray-700 text-xs font-600">
                        Purchase Value
                      </h6>
                      <p className="mb-0 text-gray-700 text-center text-base">
                        Min: {inr(demandData?.priceSummary?.minPrice)} | Max:{" "}
                        {inr(demandData?.priceSummary?.maxPrice)} | Avg:{" "}
                        {inr(demandData?.priceSummary?.avgPrice)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="mb-0">
                    <span> {grade?.name} @</span>
                    <span>
                      {" "}
                      &#8377;{price}
                      {priceLimit ? `- ${priceLimit}` : null} /{priceUnit.name}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
          <DemandDoughnut
            isExpanded={isExpanded}
            demandId={demandData?.id}
            inwardedQuantity={demandData?.inwardedQuantity}
            timeSlots={timeSlots}
          />
        </div>
        <hr className="mt-3 mb-0" />
        <DemandCardFooter
          isExpanded={isExpanded}
          demandId={id}
          totalBooked={totalBooked}
          totalDemand={totalDemand}
          demandVerified={isVerified}
        />
      </div>
    </div>
  )
}

DemandCard.defaultProps = {
  variety: { commodity: {} },
}
export default DemandCard
